import ChatbotVoiceSettings, { readConversationSettings, validConversationSettings } from './ChatbotVoiceSettings';
import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, TextField, Snackbar, Alert, MenuItem, Switch, FormControlLabel, Tooltip, IconButton, Tab, Tabs, Button } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import chatbotService from '../services/ChatbotService';
import agentService from '../services/AgentService';
import { features as featureRegistry } from '../appConfig';
import Agents from './Agents';
import KbaseWorkspace from './KbaseWorkspace';
import HelpButton from './HelpButton';

function normalizeString(value) {
    return String(value ?? '').trim();
}

function normalizeLocale(value) {
    const lowered = normalizeString(value).toLowerCase();
    if (lowered === 'ja' || lowered === 'jp' || lowered === 'ja-jp') return 'ja';
    if (lowered === 'en' || lowered === 'en-us') return 'en';
    return 'ja';
}

function escapeHtmlAttribute(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

const DEFAULT_HEADER_COLOR = '#2563eb';

function normalizeHexColor(value) {
    const raw = String(value ?? '').trim();
    if (!raw) return '';
    const withHash = raw.startsWith('#') ? raw : `#${raw}`;
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(withHash) ? withHash.toLowerCase() : raw;
}

function normalizeMultilineList(value) {
    return String(value ?? '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
}

const SYSTEM_ALLOWED_DOMAIN_HOSTS = new Set([
    'netbot.jp',
    'www.netbot.jp',
    'dashboard.netbot.jp',
]);

function normalizeDomainHost(value) {
    const raw = normalizeString(value).replace(/\/+$/, '');
    if (!raw) return '';
    try {
        const parsed = new URL(raw.includes('://') ? raw : `https://${raw}`);
        return String(parsed.hostname || '').toLowerCase();
    } catch (e) {
        return raw.split('/')[0].split(':')[0].toLowerCase();
    }
}

function filterUserManagedDomains(value) {
    const lines = Array.isArray(value)
        ? value.map((line) => normalizeString(line)).filter(Boolean)
        : normalizeMultilineList(value);
    return lines.filter((line) => !SYSTEM_ALLOWED_DOMAIN_HOSTS.has(normalizeDomainHost(line)));
}

function includesSystemAllowedDomain(value) {
    const lines = Array.isArray(value)
        ? value.map((line) => normalizeString(line)).filter(Boolean)
        : normalizeMultilineList(value);
    return lines.some((line) => SYSTEM_ALLOWED_DOMAIN_HOSTS.has(normalizeDomainHost(line)));
}

function featureLabel(featureKey) {
    const desc = normalizeString(featureRegistry?.[featureKey]);
    if (!desc) return featureKey;
    return desc
        .replace(/^Enable\s+/i, '')
        .replace(/^Allow\s+/i, '')
        .replace(/^Option\s+to\s+/i, '')
        .trim();
}

function FeatureGated({ enabled, loading, featureKey, children }) {
    const { t } = useTranslation();
    const showTooltip = loading || !enabled;
    const title = loading
        ? t('chatbotWidget.checkingPlanFeatures', 'Checking plan features…')
        : t('chatbotWidget.upgradeRequired', 'Upgrade required: {{feature}}', { feature: featureLabel(featureKey) });

    if (!showTooltip) return children;

    // Tooltip won't trigger on disabled elements unless wrapped.
    return (
        <Tooltip title={title} arrow placement="top">
            <span style={{ display: 'block', width: '100%' }}>{children}</span>
        </Tooltip>
    );
}

function isNetBotChatbot(bot) {
    return normalizeString(bot?.vendor) === 'NetBot' && Boolean(bot?.chatbotName || bot?.name);
}

function Chatbot({ settingsMode = false }) {
    const location = useLocation();
    const { t } = useTranslation();
    const { chatbotId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const requestedTab = tabParam === 'agent' || tabParam === 'agents'
        ? 'agent'
        : tabParam === 'settings'
            ? 'settings'
        : tabParam === 'kbase'
            ? 'kbase'
            : settingsMode ? 'settings' : 'kbase';
    const widgetBaseUrl = useMemo(() => {
        const raw = String(process.env.REACT_APP_WIDGET_URL || '').trim();
        const fallback = 'https://widget.chatbot.netbot.jp';
        const base = raw || fallback;
        return base.replace(/\/+$/, '');
    }, []);
    // Base URL for the bundle loaded into the Preview iframe. Defaults to the
    // hosted widget (widgetBaseUrl) but can be overridden — e.g. point it at a
    // locally-served bundle (http://localhost:5173) to test widget changes
    // before deploying. The customer-facing embed code always uses widgetBaseUrl.
    const widgetPreviewBaseUrl = useMemo(() => {
        const raw = String(process.env.REACT_APP_WIDGET_PREVIEW_URL || '').trim();
        return raw ? raw.replace(/\/+$/, '') : widgetBaseUrl;
    }, [widgetBaseUrl]);
    const widgetApiUrl = useMemo(() => {
        const raw = String(process.env.REACT_APP_CHATBOT_API_URL || process.env.REACT_APP_API_URL || '').trim();
        return raw.replace(/\/+$/, '');
    }, []);
    const [chatbotName, setChatbotName] = useState('');
    const [headerImageUrl, setHeaderImageUrl] = useState('');
    const [avatarImageUrl, setAvatarImageUrl] = useState('');
    const [centerImageUrl, setCenterImageUrl] = useState('');
    const [imageUploading, setImageUploading] = useState('');
    const [header, setHeader] = useState('');
    const [headerColor, setHeaderColor] = useState('');
    const [description, setDescription] = useState('');
    const [bubbleTextLine1, setBubbleTextLine1] = useState('');
    const [bubbleTextLine2, setBubbleTextLine2] = useState('');
    const [welcomeMessage, setWelcomeMessage] = useState('');
    const [locale, setLocale] = useState('ja');
    const [footerText, setFooterText] = useState('');
    const [footerUrl, setFooterUrl] = useState('');
    const [attachedAgentId, setAttachedAgentId] = useState('');
    const [attachedKbaseId, setAttachedKbaseId] = useState('');
    const [attachedKbaseLoading, setAttachedKbaseLoading] = useState(false);
    const [hidePoweredBy, setHidePoweredBy] = useState(false);
    const [enableLiveAgent, setEnableLiveAgent] = useState(false);
    const [placeholder, setPlaceholder] = useState('');
    const [launchType, setLaunchType] = useState('Widget');
    const [supportsVoice, setSupportsVoice] = useState(false);
    const [conversationSettings, setConversationSettings] = useState(() => readConversationSettings());
    const [feedbackType, setFeedbackType] = useState('None');
    const [suggestionsText, setSuggestionsText] = useState('');
    const [domainsText, setDomainsText] = useState('');
    const [saving, setSaving] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [hydrated, setHydrated] = useState(false);
    const [chatbotOptions, setChatbotOptions] = useState([]);
    const [chatbotOptionsLoading, setChatbotOptionsLoading] = useState(false);
    const [chatbotOptionsError, setChatbotOptionsError] = useState('');

    const [featuresLoading, setFeaturesLoading] = useState(false);
    const [enabledFeatures, setEnabledFeatures] = useState(() => new Set());
    const [featuresError, setFeaturesError] = useState(false);

    const autosaveTimerRef = useRef(null);
    const lastSavedPayloadRef = useRef('');
    const lastSuccessToastAtRef = useRef(0);
    const previewIframeRef = useRef(null);
    const [previewReady, setPreviewReady] = useState(false);
    const activeChatbotId = normalizeString(chatbotId) || normalizeString(chatbotOptions[0]?.chatbotId);
    const activeChatbotOption = chatbotOptions.find((bot) => normalizeString(bot.chatbotId) === activeChatbotId);
    const selectorHasActiveChatbot = Boolean(activeChatbotOption);
    const selectorOptionCount = chatbotOptions.length + (activeChatbotId && !selectorHasActiveChatbot ? 1 : 0);

    const normalizedLaunchType = useMemo(() => {
        const raw = String(launchType || '').trim();
        const lowered = raw.toLowerCase();

        if (lowered === 'widget') return 'Widget';
        if (lowered === 'popover') return 'Popover';
        if (lowered === 'embed' || lowered === 'ebed') return 'Embed';

        return 'Widget';
    }, [launchType]);

    const normalizedFeedbackType = useMemo(() => {
        const raw = String(feedbackType || '').trim();
        const lowered = raw.toLowerCase();

        if (!lowered || lowered === 'none') return 'None';
        if (lowered === 'thumbs' || lowered === 'thumb') return 'Thumbs';
        if (lowered === 'star' || lowered === 'stars') return 'Stars';

        return 'None';
    }, [feedbackType]);

    const normalizedLocale = useMemo(() => normalizeLocale(locale), [locale]);

    const embedCode = useMemo(() => {
        return `<script src="${widgetBaseUrl}/bundle.iife.js?v=1" data-chatbot-id="${activeChatbotId}" async>
</script>`;
    }, [activeChatbotId, widgetBaseUrl]);

    const previewSrcDoc = useMemo(() => {
        if (!activeChatbotId) return '';

        const scriptAttrs = [
            `src="${escapeHtmlAttribute(`${widgetPreviewBaseUrl}/bundle.iife.js?v=1`)}"`,
            `data-chatbot-id="${escapeHtmlAttribute(activeChatbotId)}"`,
            widgetApiUrl ? `data-api-url="${escapeHtmlAttribute(widgetApiUrl)}"` : '',
            'async',
        ].filter(Boolean).join(' ');

        return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    html, body {
      width: 100%;
      height: 100%;
      margin: 0;
      overflow: hidden;
      background: #f4f0e8;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif;
    }
  </style>
</head>
<body>
  <script ${scriptAttrs}></script>
</body>
</html>`;
    }, [activeChatbotId, widgetApiUrl, widgetPreviewBaseUrl]);

    // Live config pushed into the preview iframe on every edit, so the widget
    // reflects unsaved changes instantly (no autosave, no reload). Shape mirrors
    // the widget's ChatbotConfig.
    const previewConfig = useMemo(() => ({
        chatbotId: activeChatbotId,
        chatbotName,
        headerImageUrl,
        avatarImageUrl,
        centerImageUrl,
        header,
        headerColor: normalizeHexColor(headerColor),
        description,
        bubbleTextLine1,
        bubbleTextLine2,
        welcomeMessage,
        locale: normalizedLocale,
        footerText,
        footerUrl,
        hidePoweredBy,
        enableLiveAgent,
        placeholder,
        launchType: normalizedLaunchType,
        ...conversationSettings,
        feedbackType: normalizedFeedbackType,
        initSuggestions: normalizeMultilineList(suggestionsText),
    }), [
        activeChatbotId,
        chatbotName,
        headerImageUrl,
        avatarImageUrl,
        centerImageUrl,
        header,
        headerColor,
        description,
        bubbleTextLine1,
        bubbleTextLine2,
        welcomeMessage,
        normalizedLocale,
        footerText,
        footerUrl,
        hidePoweredBy,
        enableLiveAgent,
        placeholder,
        normalizedLaunchType,
        conversationSettings,
        normalizedFeedbackType,
        suggestionsText,
    ]);

    const postPreviewConfig = useCallback(() => {
        const frame = previewIframeRef.current;
        if (!frame || !frame.contentWindow || !activeChatbotId) return;
        // Push config only — don't force the widget open, so it stays minimized
        // by default and editing (e.g. the bubble lines) never auto-opens it.
        // The user can still click the launcher to preview the open chat.
        frame.contentWindow.postMessage(
            { source: 'netbot-preview', type: 'config', config: previewConfig },
            '*',
        );
    }, [previewConfig, activeChatbotId]);

    useEffect(() => {
        let cancelled = false;
        const loadChatbotOptions = async () => {
            setChatbotOptionsLoading(true);
            setChatbotOptionsError('');
            try {
                const data = await chatbotService.getChatbots();
                if (cancelled) return;
                setChatbotOptions(Array.isArray(data) ? data.filter(isNetBotChatbot) : []);
            } catch (e) {
                console.error('Failed to load chatbot options', e);
                if (!cancelled) {
                    setChatbotOptions([]);
                    setChatbotOptionsError(t('chatbotWidget.failedToLoadChatbots', 'Failed to load chatbots'));
                }
            } finally {
                if (!cancelled) setChatbotOptionsLoading(false);
            }
        };

        loadChatbotOptions();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const loadChatbot = async () => {
            try {
                const data = await chatbotService.getChatbots();
                const found = Array.isArray(data)
                    ? data.filter(isNetBotChatbot).find((c) => c.chatbotId === activeChatbotId)
                    : null;
                if (found) {
                    const initialName = found.chatbotName || found.name || '';
                    const initialImageUrl = found.imageUrl || '';
                    const initialHeaderImageUrl = found.headerImageUrl || initialImageUrl;
                    const initialAvatarImageUrl = found.avatarImageUrl || initialImageUrl;
                    const initialCenterImageUrl = found.centerImageUrl || initialImageUrl;
                    const initialHeader = found.header || '';
                    const initialHeaderColor = normalizeHexColor(found.headerColor || '');
                    const initialDescription = found.description || '';
                    const initialBubbleTextLine1 = found.bubbleTextLine1 || '';
                    const initialBubbleTextLine2 = found.bubbleTextLine2 || '';
                    const initialWelcomeMessage = found.welcomeMessage || '';
                    const initialLocale = normalizeLocale(found.locale);
                    const initialFooterText = found.footerText || '';
                    const initialFooterUrl = found.footerUrl || '';
                    const initialAgentId = normalizeString(found.agentId ?? found.agent_id ?? found.agent?.agentId ?? found.payload?.agentId ?? '');
                    const initialHidePoweredBy = Boolean(found.hidePoweredBy);
                    const initialEnableLiveAgent = Boolean(found.enableLiveAgent);
                    const initialPlaceholder = found.placeholder || '';
                    const initialLaunchType = found.launchType || 'Widget';
                    const initialFeedbackType = found.feedbackType || 'None';
                    const initialSuggestions = Array.isArray(found.initSuggestions)
                        ? found.initSuggestions.map((l) => String(l ?? '').trim()).filter((l) => l.length > 0)
                        : [];
                    const initialDomains = filterUserManagedDomains(found.domains);

                    setChatbotName(initialName);
                    setHeaderImageUrl(initialHeaderImageUrl);
                    setAvatarImageUrl(initialAvatarImageUrl);
                    setCenterImageUrl(initialCenterImageUrl);
                    setHeader(initialHeader);
                    setHeaderColor(initialHeaderColor);
                    setDescription(initialDescription);
                    setBubbleTextLine1(initialBubbleTextLine1);
                    setBubbleTextLine2(initialBubbleTextLine2);
                    setWelcomeMessage(initialWelcomeMessage);
                    setLocale(initialLocale);
                    setFooterText(initialFooterText);
                    setFooterUrl(initialFooterUrl);
                    setAttachedKbaseLoading(Boolean(initialAgentId));
                    setAttachedAgentId(initialAgentId);
                    setHidePoweredBy(initialHidePoweredBy);
                    setEnableLiveAgent(initialEnableLiveAgent);
                    setPlaceholder(initialPlaceholder);
                    setLaunchType(initialLaunchType);
                    setConversationSettings(readConversationSettings(found));
                    setSupportsVoice(found.vendor === 'NetBot');
                    setFeedbackType(initialFeedbackType);
                    setSuggestionsText(initialSuggestions.join('\n'));
                    setDomainsText(initialDomains.join('\n'));

                    const raw = String(initialLaunchType || '').trim().toLowerCase();
                    const initialNormalizedLaunchType =
                        raw === 'popover'
                            ? 'Popover'
                            : raw === 'embed' || raw === 'ebed'
                                ? 'Embed'
                                : 'Widget';

                    const initialFeedbackRaw = String(initialFeedbackType || '').trim().toLowerCase();
                    const initialNormalizedFeedbackType =
                        !initialFeedbackRaw || initialFeedbackRaw === 'none'
                            ? 'None'
                            : initialFeedbackRaw === 'thumbs' || initialFeedbackRaw === 'thumb'
                                ? 'Thumbs'
                                : initialFeedbackRaw === 'star' || initialFeedbackRaw === 'stars'
                                    ? 'Stars'
                                    : 'None';

                    lastSavedPayloadRef.current = JSON.stringify({
                        chatbotName: initialName,
                        headerImageUrl: initialHeaderImageUrl,
                        avatarImageUrl: initialAvatarImageUrl,
                        centerImageUrl: initialCenterImageUrl,
                        header: initialHeader,
                        headerColor: initialHeaderColor,
                        description: initialDescription,
                        bubbleTextLine1: initialBubbleTextLine1,
                        bubbleTextLine2: initialBubbleTextLine2,
                        welcomeMessage: initialWelcomeMessage,
                        locale: initialLocale,
                        footerText: initialFooterText,
                        footerUrl: initialFooterUrl,
                        hidePoweredBy: initialHidePoweredBy,
                        enableLiveAgent: initialEnableLiveAgent,
                        placeholder: initialPlaceholder,
                        launchType: initialNormalizedLaunchType,
                        ...readConversationSettings(found),
                        feedbackType: initialNormalizedFeedbackType,
                        initSuggestions: initialSuggestions,
                        domains: initialDomains,
                    });
                } else {
                    setAttachedKbaseLoading(false);
                }
            } catch (e) {
                // If this fails, just leave fields empty; editing is optional.
                console.error('Failed to load chatbot metadata', e);
                setAttachedKbaseLoading(false);
            } finally {
                setHydrated(true);
            }
        };

        if (activeChatbotId) {
            setHydrated(false);
            setAttachedKbaseId('');
            setAttachedKbaseLoading(true);
            loadChatbot();
        }
    }, [activeChatbotId]);

    useEffect(() => {
        if (!activeChatbotId) return;

        let cancelled = false;
        const loadFeatures = async () => {
            setFeaturesLoading(true);
            setFeaturesError(false);
            try {
                const list = await chatbotService.getEnabledFeatures();
                if (cancelled) return;
                const normalized = (Array.isArray(list) ? list : [])
                    .map((f) => normalizeString(f))
                    .filter(Boolean);
                setEnabledFeatures(new Set(normalized));
            } catch (e) {
                // If feature lookup fails, don't block editing.
                console.error('Failed to load enabled features', e);
                if (!cancelled) {
                    setEnabledFeatures(new Set());
                    setFeaturesError(true);
                }
            } finally {
                if (!cancelled) setFeaturesLoading(false);
            }
        };

        loadFeatures();
        return () => {
            cancelled = true;
        };
    }, [activeChatbotId]);

    useEffect(() => {
        if (!attachedAgentId) {
            setAttachedKbaseId('');
            setAttachedKbaseLoading(false);
            return;
        }

        let cancelled = false;
        const loadAttachedAgent = async () => {
            setAttachedKbaseLoading(true);
            try {
                const agent = await agentService.getAgent(attachedAgentId);
                if (cancelled) return;
                const kbaseId = normalizeString(
                    agent?.kbaseId
                    ?? agent?.kbase?.kbaseId
                    ?? agent?.kbase?.id
                    ?? agent?.knowledgeBaseId
                    ?? agent?.knowledge_base_id
                );
                setAttachedKbaseId(kbaseId);
            } catch (e) {
                console.error('Failed to load attached agent kbase', e);
                if (!cancelled) setAttachedKbaseId('');
            } finally {
                if (!cancelled) setAttachedKbaseLoading(false);
            }
        };

        loadAttachedAgent();
        return () => {
            cancelled = true;
        };
    }, [attachedAgentId]);

    const hasFeature = (key) => {
        if (featuresError) return true;
        if (featuresLoading) return false;
        return enabledFeatures.has(key);
    };
    const canDisplayAgent = settingsMode || hasFeature('chatbot.agent');
    const selectedTab = settingsMode
        ? 'settings'
        : requestedTab === 'agent' && !canDisplayAgent
            ? 'settings'
            : requestedTab;
    const showChatbotTabs = false;

    useEffect(() => {
        if (!activeChatbotId) return;
        if (!hydrated || !validConversationSettings(conversationSettings)) return;

        const lines = normalizeMultilineList(suggestionsText);
        const domains = filterUserManagedDomains(domainsText);

        const payload = {
            chatbotName,
            headerImageUrl,
            avatarImageUrl,
            centerImageUrl,
            header,
            headerColor: normalizeHexColor(headerColor),
            description,
            bubbleTextLine1,
            bubbleTextLine2,
            welcomeMessage,
            locale: normalizedLocale,
            footerText,
            footerUrl,
            hidePoweredBy,
            enableLiveAgent,
            placeholder,
            launchType: normalizedLaunchType,
            ...conversationSettings,
            feedbackType: normalizedFeedbackType,
            initSuggestions: lines,
            domains,
        };

        const payloadKey = JSON.stringify(payload);
        if (payloadKey === lastSavedPayloadRef.current) return;

        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
        }

        autosaveTimerRef.current = setTimeout(async () => {
            setSaving(true);
            try {
                await chatbotService.updateChatbot(activeChatbotId, payload);
                lastSavedPayloadRef.current = payloadKey;
                setSuggestionsText(lines.join('\n'));
                setDomainsText(domains.join('\n'));
                // Preview updates live via postMessage; no iframe reload needed.
                // Throttle success toasts so autosave doesn't feel too "chatty" while typing.
                const now = Date.now();
                if (now - (lastSuccessToastAtRef.current || 0) > 5000) {
                    lastSuccessToastAtRef.current = now;
                    setSnackbarSeverity('success');
                    setSnackbarMessage(t('chatbotWidget.settingsSaved', 'Chatbot settings saved'));
                    setSnackbarOpen(true);
                }
            } catch (e) {
                console.error('Failed to save chatbot settings', e);
                setSnackbarSeverity('error');
                setSnackbarMessage(t('chatbotWidget.failedToSaveSettings', 'Failed to save chatbot settings'));
                setSnackbarOpen(true);
            } finally {
                setSaving(false);
            }
        }, 1500);

        return () => {
            if (autosaveTimerRef.current) {
                clearTimeout(autosaveTimerRef.current);
            }
        };
    }, [
        activeChatbotId,
        hydrated,
        chatbotName,
        headerImageUrl,
        avatarImageUrl,
        centerImageUrl,
        header,
        headerColor,
        description,
        bubbleTextLine1,
        bubbleTextLine2,
        welcomeMessage,
        normalizedLocale,
        footerText,
        footerUrl,
        hidePoweredBy,
        enableLiveAgent,
        placeholder,
        normalizedLaunchType,
        conversationSettings,
        normalizedFeedbackType,
        suggestionsText,
        domainsText,
    ]);

    // Handshake: the embedded widget announces readiness once its message
    // listener is attached; reply with the current config so nothing is lost to
    // a load-timing race.
    useEffect(() => {
        const handleMessage = (event) => {
            const data = event.data;
            if (!data || data.source !== 'netbot-widget-preview') return;
            if (data.type === 'ready') {
                setPreviewReady(true);
                postPreviewConfig();
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [postPreviewConfig]);

    // Push config live as the form changes (once the widget is ready).
    useEffect(() => {
        if (!previewReady) return;
        postPreviewConfig();
    }, [previewReady, postPreviewConfig]);

    // A fresh iframe (chatbot switched / reloaded) re-runs its own handshake, so
    // wait for the new widget to announce readiness again.
    useEffect(() => {
        setPreviewReady(false);
    }, [activeChatbotId]);

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const handleDomainsTextChange = (value) => {
        setDomainsText(value);
        if (includesSystemAllowedDomain(value)) {
            setSnackbarSeverity('info');
            setSnackbarMessage(t('chatbotWidget.domainAlreadyAllowed', 'That domain is already in the system default CORS list.'));
            setSnackbarOpen(true);
        }
    };

    const handleCopyEmbedCode = async () => {
        try {
            await navigator.clipboard.writeText(embedCode);
            setSnackbarSeverity('success');
            setSnackbarMessage(t('chatbotWidget.embedCodeCopied', 'Embed code copied'));
            setSnackbarOpen(true);
        } catch (e) {
            console.error('Failed to copy embed code', e);
            setSnackbarSeverity('error');
            setSnackbarMessage(t('chatbotWidget.failedToCopyEmbedCode', 'Failed to copy embed code'));
            setSnackbarOpen(true);
        }
    };

    const handleImageUpload = (imageType) => async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !activeChatbotId) return;

        const allowed = file.type === 'image/png' || file.type === 'image/jpeg';
        if (!allowed) {
            setSnackbarSeverity('error');
            setSnackbarMessage(t('chatbotWidget.onlyPngJpegAllowed', 'Only PNG and JPEG images are allowed'));
            setSnackbarOpen(true);
            return;
        }

        setImageUploading(imageType);
        try {
            const result = await chatbotService.uploadChatbotImage(activeChatbotId, file, imageType);
            const uploadedUrl = result?.[`${imageType}ImageUrl`] || result?.imageUrl || '';
            if (imageType === 'header') {
                setHeaderImageUrl(uploadedUrl);
            } else if (imageType === 'avatar') {
                setAvatarImageUrl(uploadedUrl);
            } else if (imageType === 'center') {
                setCenterImageUrl(uploadedUrl);
            }
            // New image URL flows to the preview live via postMessage.
            setSnackbarSeverity('success');
            setSnackbarMessage(t('chatbotWidget.imageUploaded', 'Chatbot image uploaded'));
            setSnackbarOpen(true);
        } catch (e) {
            console.error('Failed to upload chatbot image', e);
            setSnackbarSeverity('error');
            setSnackbarMessage(t('chatbotWidget.failedToUploadImage', 'Failed to upload chatbot image'));
            setSnackbarOpen(true);
        } finally {
            setImageUploading('');
        }
    };

    const handleChatbotChange = (event) => {
        const nextChatbotId = normalizeString(event.target.value);
        if (!nextChatbotId || nextChatbotId === activeChatbotId) return;

        const query = searchParams.toString();
        const basePath = settingsMode ? '/chatbot-settings/chatbot' : '/documents/chatbot';
        const feedbackSuffix = !settingsMode && location.pathname.endsWith('/feedback') ? '/feedback' : '';
        navigate(`${basePath}/${encodeURIComponent(nextChatbotId)}${feedbackSuffix}${query ? `?${query}` : ''}`);
    };

    const chatbotSelector = !settingsMode && selectorOptionCount > 1 ? (
            <TextField
                select
                label={t('chatbotWidget.chatbot', 'Chatbot')}
                size="small"
                value={activeChatbotId}
                onChange={handleChatbotChange}
                disabled={chatbotOptionsLoading || chatbotOptions.length === 0}
                sx={{ minWidth: { xs: '100%', sm: 342 } }}
            >
                {activeChatbotId && !selectorHasActiveChatbot ? (
                    <MenuItem value={activeChatbotId}>
                        {chatbotName || activeChatbotId}
                    </MenuItem>
                ) : null}
                {chatbotOptions.map((bot) => {
                    const id = normalizeString(bot.chatbotId);
                    return (
                        <MenuItem key={id} value={id}>
                            {bot.chatbotName || bot.name || id}
                        </MenuItem>
                    );
                })}
            </TextField>
    ) : null;

    const handleTabChange = (event, nextTab) => {
        if (nextTab === 'agent' && !canDisplayAgent) return;

        if (settingsMode) {
            setSearchParams(nextTab === 'agent' ? { tab: 'agent' } : {});
            return;
        }

        if (nextTab === 'kbase') {
            setSearchParams({});
            return;
        }

        if (nextTab === 'agent' || nextTab === 'settings') {
            setSearchParams({ tab: nextTab });
            return;
        }

        setSearchParams({});
    };

    return (
        <Box
            sx={{
                m: settingsMode ? -3 : -3,
                p: settingsMode ? 2 : 1.5,
                minHeight: settingsMode ? 'auto' : 'calc(100vh - 48px)',
                display: 'flex',
                flexDirection: 'column',
                gap: settingsMode ? 0 : 1.5,
                backgroundColor: settingsMode ? 'transparent' : 'background.paper',
            }}
        >
            {!activeChatbotId ? (
                <Alert severity={chatbotOptionsError ? 'error' : 'info'}>
                    {chatbotOptionsLoading
                        ? t('chatbotWidget.loadingChatbots', 'Loading chatbots…')
                        : chatbotOptionsError || t('chatbotWidget.noChatbotsFound', 'No NetBot chatbots found.')}
                </Alert>
            ) : (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, mb: 2 }}>
                        <Typography variant="h5" fontWeight="bold">
                            {settingsMode ? t('sideBar.chatbotMenu', 'Chatbots') : t('sideBar.documents', 'Documents')}
                        </Typography>
                        <HelpButton />
                    </Box>
                    {showChatbotTabs ? (
                        <Tabs
                            value={selectedTab}
                            onChange={handleTabChange}
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            {canDisplayAgent ? (
                                <Tab value="agent" label={t('chatbotWidget.agentTab', 'Agent')} />
                            ) : null}
                            {settingsMode ? (
                                <Tab value="settings" label={t('chatbotWidget.chatbot', 'Chatbot')} />
                            ) : (
                                <Tab value="kbase" label={t('chatbotWidget.documents', 'Documents')} />
                            )}
                            {!settingsMode ? (
                                <Tab value="settings" label={t('chatbotWidget.settingsTab', 'Settings')} />
                            ) : null}
                        </Tabs>
                    ) : null}

                    {!settingsMode && !showChatbotTabs ? (
                        !hydrated || attachedKbaseLoading ? (
                            <Alert severity="info">
                                {t('chatbotWidget.loadingDocuments', 'Loading documents…')}
                            </Alert>
                        ) : attachedKbaseId ? (
                            <KbaseWorkspace embedded kbaseId={attachedKbaseId} toolbarStart={chatbotSelector} />
                        ) : (
                            <Alert severity="info">
                                {t('chatbotWidget.noKbaseAttached', "No KBase is attached to this chatbot's agent.")}
                            </Alert>
                        )
                    ) : selectedTab === 'agent' ? (
                        <Agents embedded agentId={attachedAgentId} />
                    ) : selectedTab === 'kbase' ? (
                        !hydrated || attachedKbaseLoading ? (
                            <Alert severity="info">
                                {t('chatbotWidget.loadingDocuments', 'Loading documents…')}
                            </Alert>
                        ) : attachedKbaseId ? (
                            <KbaseWorkspace embedded kbaseId={attachedKbaseId} toolbarStart={chatbotSelector} />
                        ) : (
                            <Alert severity="info">
                                {t('chatbotWidget.noKbaseAttached', "No KBase is attached to this chatbot's agent.")}
                            </Alert>
                        )
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'minmax(0, 1fr)',
                                    lg: 'minmax(0, 640px) minmax(460px, 1fr)',
                                },
                                gap: 3,
                                alignItems: 'start',
                                maxWidth: '100%',
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Paper sx={{ p: 2 }} elevation={1}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                    {t('chatbotWidget.chatbotSettings', 'Chatbot Settings')}
                                </Typography>
                                <TextField
                                    label={t('chatbotWidget.name', 'Name')}
                                    fullWidth
                                    size="small"
                                    sx={{ mb: 2 }}
                                    value={chatbotName}
                                    onChange={(e) => setChatbotName(e.target.value)}
                                    disabled={saving || !hydrated}
                                />
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                        {t('chatbotWidget.chatbotImages', 'Chatbot Images')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {[
                                            { type: 'header', label: t('chatbotWidget.headerImage', 'Header Image'), value: headerImageUrl },
                                            { type: 'center', label: t('chatbotWidget.centerImage', 'Center Image'), value: centerImageUrl },
                                            { type: 'avatar', label: t('chatbotWidget.conversationAvatar', 'Conversation Avatar'), value: avatarImageUrl },
                                        ].map((item) => (
                                            <Box key={item.type} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box
                                                    sx={{
                                                        width: 64,
                                                        height: 64,
                                                        borderRadius: '50%',
                                                        overflow: 'hidden',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        backgroundColor: 'grey.100',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'text.secondary',
                                                        fontWeight: 700,
                                                        flex: '0 0 auto',
                                                    }}
                                                >
                                                    {item.value ? (
                                                        <Box
                                                            component="img"
                                                            src={item.value}
                                                            alt={item.label}
                                                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        chatbotName?.trim()?.charAt(0)?.toUpperCase() || 'N'
                                                    )}
                                                </Box>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        component="label"
                                                        disabled={saving || Boolean(imageUploading) || !hydrated}
                                                        sx={{ textTransform: 'none' }}
                                                    >
                                                        {imageUploading === item.type ? t('chatbotWidget.uploading', 'Uploading...') : t('chatbotWidget.uploadPngJpeg', 'Upload PNG or JPEG')}
                                                        <input
                                                            hidden
                                                            type="file"
                                                            accept="image/png,image/jpeg"
                                                            onChange={handleImageUpload(item.type)}
                                                        />
                                                    </Button>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                        <FeatureGated enabled={hasFeature('chatbot.custom_header')} loading={featuresLoading} featureKey="chatbot.custom_header">
                            <TextField
                                label={t('chatbotWidget.header', 'Header')}
                                fullWidth
                                size="small"
                                sx={{ mb: 2 }}
                                value={header}
                                onChange={(e) => setHeader(e.target.value)}
                                disabled={saving || !hydrated || featuresLoading || !hasFeature('chatbot.custom_header')}
                            />
                        </FeatureGated>
                        <FeatureGated enabled={hasFeature('chatbot.custom_header')} loading={featuresLoading} featureKey="chatbot.custom_header">
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                    {t('chatbotWidget.headerColor', 'Header Bar Color')}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box
                                        component="input"
                                        type="color"
                                        value={normalizeHexColor(headerColor) || DEFAULT_HEADER_COLOR}
                                        onChange={(e) => setHeaderColor(e.target.value)}
                                        disabled={saving || !hydrated || featuresLoading || !hasFeature('chatbot.custom_header')}
                                        aria-label={t('chatbotWidget.headerColor', 'Header Bar Color')}
                                        sx={{
                                            width: 48,
                                            height: 40,
                                            p: 0,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            background: 'none',
                                            cursor: 'pointer',
                                            flex: '0 0 auto',
                                        }}
                                    />
                                    <TextField
                                        size="small"
                                        placeholder={DEFAULT_HEADER_COLOR}
                                        value={headerColor}
                                        onChange={(e) => setHeaderColor(e.target.value)}
                                        disabled={saving || !hydrated || featuresLoading || !hasFeature('chatbot.custom_header')}
                                        sx={{ maxWidth: 160 }}
                                    />
                                    {headerColor ? (
                                        <Button
                                            size="small"
                                            onClick={() => setHeaderColor('')}
                                            disabled={saving || !hydrated || featuresLoading || !hasFeature('chatbot.custom_header')}
                                        >
                                            {t('chatbotWidget.resetColor', 'Reset')}
                                        </Button>
                                    ) : null}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {t('chatbotWidget.headerColorHelper', 'Color of the chat header bar. Suggestion buttons use a matching lighter shade. Leave blank to use the default.')}
                                </Typography>
                            </Box>
                        </FeatureGated>
                        <TextField
                            label={t('chatbotWidget.description', 'Description')}
                            multiline
                            fullWidth
                            minRows={2}
                            size="small"
                            sx={{ mb: 2 }}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={saving || !hydrated}
                        />
                        <TextField
                            label={t('chatbotWidget.welcomeMessage', 'Welcome Message')}
                            multiline
                            fullWidth
                            minRows={3}
                            size="small"
                            sx={{ mb: 2 }}
                            value={welcomeMessage}
                            onChange={(e) => setWelcomeMessage(e.target.value)}
                            disabled={saving || !hydrated}
                        />
                        <TextField
                            label={t('chatbotWidget.locale', 'Locale')}
                            select
                            fullWidth
                            size="small"
                            sx={{ mb: 2 }}
                            value={normalizedLocale}
                            onChange={(e) => setLocale(e.target.value)}
                            disabled={saving || !hydrated}
                        >
                            <MenuItem value="en">{t('chatbotWidget.localeEnglish', 'English')}</MenuItem>
                            <MenuItem value="ja">{t('chatbotWidget.localeJapanese', 'Japanese')}</MenuItem>
                        </TextField>
                        <TextField
                            label={t('chatbotWidget.minimizedBubbleLine1', 'Minimized Bubble Line 1')}
                            fullWidth
                            size="small"
                            sx={{ mb: 2 }}
                            value={bubbleTextLine1}
                            onChange={(e) => setBubbleTextLine1(e.target.value)}
                            disabled={saving || !hydrated}
                        />
                        <TextField
                            label={t('chatbotWidget.minimizedBubbleLine2', 'Minimized Bubble Line 2')}
                            fullWidth
                            size="small"
                            sx={{ mb: 2 }}
                            value={bubbleTextLine2}
                            onChange={(e) => setBubbleTextLine2(e.target.value)}
                            disabled={saving || !hydrated}
                        />
                        <FeatureGated enabled={hasFeature('chatbot.custom_placeholder')} loading={featuresLoading} featureKey="chatbot.custom_placeholder">
                            <TextField
                                label={t('chatbotWidget.placeholder', 'Placeholder')}
                                fullWidth
                                size="small"
                                sx={{ mb: 2 }}
                                value={placeholder}
                                onChange={(e) => setPlaceholder(e.target.value)}
                                disabled={saving || !hydrated || featuresLoading || !hasFeature('chatbot.custom_placeholder')}
                            />
                        </FeatureGated>

                        <ChatbotVoiceSettings value={conversationSettings} onChange={setConversationSettings}
                            disabled={saving || !hydrated} voiceAllowed={supportsVoice && hasFeature('voice_enable')} />
                        <TextField
                            label={t('chatbotWidget.launchType', 'Launch Type')}
                            select
                            fullWidth
                            size="small"
                            sx={{ mb: 2 }}
                            value={normalizedLaunchType}
                            onChange={(e) => setLaunchType(e.target.value)}
                            disabled={saving || !hydrated}
                        >
                            <MenuItem value="Widget">{t('chatbotWidget.launchTypeWidget', 'Widget')}</MenuItem>
                            <MenuItem value="Popover">{t('chatbotWidget.launchTypePopover', 'Popover')}</MenuItem>
                            <MenuItem value="Embed">{t('chatbotWidget.launchTypeEmbed', 'Embed')}</MenuItem>
                        </TextField>

                        <FeatureGated enabled={hasFeature('chatbot.feedback')} loading={featuresLoading} featureKey="chatbot.feedback">
                            <TextField
                                label={t('chatbotWidget.feedbackType', 'Feedback Type')}
                                select
                                fullWidth
                                size="small"
                                sx={{ mb: 2 }}
                                value={normalizedFeedbackType}
                                onChange={(e) => setFeedbackType(e.target.value)}
                                disabled={saving || !hydrated || featuresLoading || !hasFeature('chatbot.feedback')}
                            >
                                <MenuItem value="None">{t('chatbotWidget.feedbackTypeNone', 'None')}</MenuItem>
                                <MenuItem value="Thumbs">{t('chatbotWidget.feedbackTypeThumbs', 'Thumbs')}</MenuItem>
                                <MenuItem value="Stars">{t('chatbotWidget.feedbackTypeStars', 'Stars')}</MenuItem>
                            </TextField>
                        </FeatureGated>

                        <FeatureGated enabled={hasFeature('chatbot.suggestions')} loading={featuresLoading} featureKey="chatbot.suggestions">
                            <TextField
                                label={t('chatbotWidget.initialSuggestions', 'Initial Suggestions (one per line)')}
                                multiline
                                fullWidth
                                minRows={6}
                                placeholder={'NetBotとは?\nNetBotの提供サービスは？\nNetBotの独自技術は何ですか？'}
                                InputLabelProps={{ shrink: true }}
                                sx={{ mb: 2, '& textarea::placeholder': { whiteSpace: 'pre-line' } }}
                                value={suggestionsText}
                                onChange={(e) => setSuggestionsText(e.target.value)}
                                disabled={saving || !hydrated || featuresLoading || !hasFeature('chatbot.suggestions')}
                            />
                        </FeatureGated>

                        <TextField
                            label={t('chatbotWidget.allowedDomains', 'Allowed Domains (one per line)')}
                            multiline
                            fullWidth
                            minRows={4}
                            placeholder={'example.com\napp.example.com\nhttps://localhost:3000'}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 2, '& textarea::placeholder': { whiteSpace: 'pre-line' } }}
                            value={domainsText}
                            onChange={(e) => handleDomainsTextChange(e.target.value)}
                            disabled={saving || !hydrated}
                            helperText={t('chatbotWidget.allowedDomainsHelper', 'NetBot domains are allowed by the system. Add only customer domains here.')}
                        />

                        <FeatureGated enabled={hasFeature('chatbot.custom_footer')} loading={featuresLoading} featureKey="chatbot.custom_footer">
                            <TextField
                                label={t('chatbotWidget.footerText', 'Footer Text')}
                                fullWidth
                                size="small"
                                sx={{ mb: 2 }}
                                value={footerText}
                                onChange={(e) => setFooterText(e.target.value)}
                                disabled={saving || !hydrated || featuresLoading || !hasFeature('chatbot.custom_footer')}
                            />
                        </FeatureGated>

                        <FeatureGated enabled={hasFeature('chatbot.custom_footer')} loading={featuresLoading} featureKey="chatbot.custom_footer">
                            <TextField
                                label={t('chatbotWidget.footerUrl', 'Footer URL')}
                                fullWidth
                                size="small"
                                sx={{ mb: 2 }}
                                value={footerUrl}
                                onChange={(e) => setFooterUrl(e.target.value)}
                                disabled={saving || !hydrated || featuresLoading || !hasFeature('chatbot.custom_footer')}
                            />
                        </FeatureGated>

                        <FeatureGated enabled={hasFeature('chatbot.hide_powered_by')} loading={featuresLoading} featureKey="chatbot.hide_powered_by">
                            <FormControlLabel
                                sx={{ mb: 2, display: 'flex' }}
                                label={t('chatbotWidget.hidePoweredBy', 'Hide Powered By NetBot')}
                                control={
                                    <Switch
                                        size="small"
                                        checked={hidePoweredBy}
                                        onChange={(e) => setHidePoweredBy(e.target.checked)}
                                        disabled={saving || !hydrated || featuresLoading || !hasFeature('chatbot.hide_powered_by')}
                                    />
                                }
                            />
                        </FeatureGated>

                        <FeatureGated enabled={hasFeature('chatbot.live_agent')} loading={featuresLoading} featureKey="chatbot.live_agent">
                            <FormControlLabel
                                sx={{ mb: 2, display: 'flex' }}
                                label={t('chatbotWidget.enableLiveAgent', 'Enable Live Agent')}
                                control={
                                    <Switch
                                        size="small"
                                        checked={enableLiveAgent}
                                        onChange={(e) => setEnableLiveAgent(e.target.checked)}
                                        disabled={saving || !hydrated || featuresLoading || !hasFeature('chatbot.live_agent')}
                                    />
                                }
                            />
                        </FeatureGated>
                    </Paper>
                            <Paper sx={{ mt: 2, p: 2, backgroundColor: 'grey.100' }} elevation={1}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                    {t('chatbotWidget.embedCode', 'Embed Code')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {t('chatbotWidget.embedCodeInstruction', 'Copy and paste this snippet into the page where you want to embed this chatbot.')}
                                </Typography>
                                <Box sx={{ position: 'relative' }}>
                                    <TextField
                                        value={embedCode}
                                        multiline
                                        fullWidth
                                        minRows={6}
                                        maxRows={10}
                                        InputProps={{
                                            readOnly: true,
                                            sx: {
                                                fontFamily: 'monospace',
                                                fontSize: '0.8rem',
                                                backgroundColor: '#2b2b2b',
                                                color: 'common.white',
                                                pr: 5,
                                            },
                                        }}
                                    />
                                    <Tooltip title={t('chatbotWidget.copyEmbedCode', 'Copy embed code')} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={handleCopyEmbedCode}
                                            aria-label={t('chatbotWidget.copyEmbedCode', 'Copy embed code')}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                color: 'common.white',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                },
                                            }}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Paper>
                            </Box>
                            {settingsMode ? (
                                <Paper
                                    sx={{
                                        p: 2,
                                        position: { lg: 'sticky' },
                                        top: { lg: 24 },
                                        minWidth: 0,
                                        width: '100%',
                                    }}
                                    elevation={1}
                                >
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                        {t('chatbotWidget.preview', 'Preview')}
                                    </Typography>
                                    <Box
                                        component="iframe"
                                        allow="microphone; autoplay"
                                        ref={previewIframeRef}
                                        title={t('chatbotWidget.previewTitle', 'Chatbot preview')}
                                        srcDoc={previewSrcDoc}
                                        onLoad={postPreviewConfig}
                                        sx={{
                                            display: 'block',
                                            width: '100%',
                                            height: { xs: 560, lg: 'calc(100vh - 116px)' },
                                            minHeight: 520,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            backgroundColor: '#f4f0e8',
                                        }}
                                    />
                                </Paper>
                            ) : null}
                </Box>
                    )}
                </>
            )}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>{snackbarMessage}</Alert>
            </Snackbar>
        </Box>
    );
}

export default Chatbot;
